import {
	StepIndicator,
	type StepIndicatorOrientation,
	type StepIndicatorProps,
	type StepIndicatorStatus,
} from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement, ReactNode } from 'react'

/**
 * One wizard step.
 *
 * @property id - Stable identity, passed back to `onStepSelect`.
 * @property label - The step's name (product copy, supplied by the consumer).
 * @property status - Its progress, drives the indicator styling.
 * @property selectable - Whether the user may jump to it; a non-selectable step is not activatable.
 */
export interface WizardStep {
	id: string
	label: string
	status: StepIndicatorStatus
	selectable: boolean
}

/**
 * Localizable copy of {@link WizardShell}; every key is optional and the consumer
 * owns all visible strings (REQ-005). `region`/`status` flow to the composed
 * {@link StepIndicator}; `back`/`next` name the navigation controls.
 */
export interface WizardShellLabels {
	region?: string
	status?: Partial<Record<StepIndicatorStatus, string>>
	back?: string
	next?: string
}

/**
 * Props for the {@link WizardShell} pattern.
 *
 * @property steps - The ordered steps shown in the indicator.
 * @property currentId - The open step's id (`aria-current="step"`).
 * @property orientation - Step-indicator layout; defaults to horizontal (a top step bar).
 * @property onStepSelect - Called with a selectable step's id when it is activated; omit for a read-only indicator.
 * @property onBack - Goes to the previous step; no Back control when absent.
 * @property onNext - Advances; no Next control when absent.
 * @property nextDisabled - Disables Next (e.g. the step is incomplete or a request is in flight).
 * @property error - A message shown as an assertive alert above the content; no alert when absent.
 * @property children - The active step's content.
 * @property emptyFallback - Rendered in the content area when there is no `children`.
 * @property labels - Localized copy; each omitted key falls back to the composed defaults.
 */
export interface WizardShellProps {
	steps: WizardStep[]
	currentId: string
	orientation?: StepIndicatorOrientation
	onStepSelect?: (id: string) => void
	onBack?: () => void
	onNext?: () => void
	nextDisabled?: boolean
	error?: string | null
	children?: ReactNode
	emptyFallback?: ReactNode
	labels?: WizardShellLabels
}

/** Built-in English copy for the navigation controls; each is overridable via `labels`. */
const DEFAULT_LABELS: Required<Pick<WizardShellLabels, 'back' | 'next'>> = {
	back: 'Back',
	next: 'Next',
}

/** Back / Next under the step content; nothing when the step offers neither (TO-125 parity). */
function StepNavigation({
	onBack,
	onNext,
	nextDisabled,
	backLabel,
	nextLabel,
}: {
	onBack?: () => void
	onNext?: () => void
	nextDisabled?: boolean
	backLabel: string
	nextLabel: string
}): ReactElement | null {
	if (!onBack && !onNext) return null
	return (
		<div className="flex justify-between gap-ds-3">
			{onBack ? (
				<Button variant="secondary" onPress={onBack}>
					{backLabel}
				</Button>
			) : (
				<span />
			)}
			{onNext ? (
				<Button isDisabled={nextDisabled} onPress={onNext}>
					{nextLabel}
				</Button>
			) : null}
		</div>
	)
}

/**
 * A business-agnostic multi-step shell: a {@link StepIndicator}, a Back/Next bar,
 * an error region and the active step's content. One shell for every wizard
 * (UX interaction standard §8; TO-652).
 *
 * @remarks
 * - **Pure & business-agnostic:** props in → JSX out. It holds no step labels,
 *   routes or domain rules; all copy is consumer-supplied (REQ-005). It imports
 *   no `ui-domain`, router, or data-fetching — the `ui-recipes → ui-domain`
 *   forbidden edge in the ui-layer policy enforces this structurally.
 * - **Jump-to-step** fires `onStepSelect` only for steps the consumer marked
 *   selectable; a non-selectable step is not activatable.
 *
 * @see eslint-rules/ui-layer-policy.json (`ui-recipes → ui-domain` forbidden edge)
 * @see packages/ui-recipes/src/wizard-shell/wizard-shell.stories.tsx
 */
export function WizardShell({
	steps,
	currentId,
	orientation = 'horizontal',
	onStepSelect,
	onBack,
	onNext,
	nextDisabled,
	error,
	children,
	emptyFallback,
	labels,
}: WizardShellProps): ReactElement {
	const indicatorLabels: StepIndicatorProps['labels'] =
		labels && (labels.region || labels.status) ? { region: labels.region, status: labels.status } : undefined
	const items = steps.map((step) => ({ ...step, optional: false }))
	return (
		<section className="flex flex-col gap-ds-4">
			<StepIndicator
				items={items}
				currentId={currentId}
				orientation={orientation}
				onSelect={onStepSelect}
				labels={indicatorLabels}
			/>
			{error ? (
				<p role="alert" aria-live="assertive" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
			{children ?? emptyFallback ?? null}
			<StepNavigation
				onBack={onBack}
				onNext={onNext}
				nextDisabled={nextDisabled}
				backLabel={labels?.back ?? DEFAULT_LABELS.back}
				nextLabel={labels?.next ?? DEFAULT_LABELS.next}
			/>
		</section>
	)
}
